import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { loginQueryKeys } from "@features/auth/hooks/queries/keys/loginQueryKeys";
import { fetchLoginInfo } from "@features/auth/api/loginApi";

// meta.requiresAuth: true인(isLogin: true일 때만 호출해야 하는) 호출은 제거/취소 처리
async function forceLogout(queryClient: QueryClient) {
  await queryClient.cancelQueries({
    predicate: (q) => q.meta?.requiresAuth === true,
  });
  queryClient.removeQueries({
    predicate: (q) => q.meta?.requiresAuth === true,
  });
}

export const useFetchLoginInfo = () => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["loginInfo"],
    queryFn: fetchLoginInfo,
    select: (data) => {
      if (!data) {
        return data;
      }

      const { ncfzUserInfo, playncUserInfo } = data;

      // logout일 때 이전에 요청했던 쿼리 제거
      if (!(ncfzUserInfo.login || playncUserInfo.login)) {
        forceLogout(queryClient);
      }

      return {
        isLogin: (ncfzUserInfo.login || playncUserInfo.login) ?? false,
        account: { ...data },
      };
    },
    // staletime 10분으로 설정하여 그동안 자동 refetch 트리거 와도 api 요청하지 않음
    // plaync 계정 로그인 세션 유지 시간: 1시간
    // plaync 로그인/로그아웃 시 페이지 새로고침되어 stale 상태 초기화, ncfz 로그인/로그아웃 시 invalidateQueries()로 stale로 상태 변경함
    staleTime: 1000 * 60 * 10,

    // 자동 트리거
    refetchOnMount: true, // 마운트 시 (기본: true → stale일 때만 refetch, 'always' → 항상 refetch)
    refetchOnWindowFocus: "always", // 윈도 포커스 시 (기본: true → stale일 때만, 'always' → 항상 refetch)
    refetchOnReconnect: "always", // 네트워크 재연결 시 (기본: true → stale일 때만, 'always' → 항상 refetch)
    // refetchInterval: 1000 * 60 * 5, // 인터벌 리패치 시(폴링) (ms, 기본 꺼짐)
    // refetchIntervalInBackground: false // 인터벌 리패치 시(폴링) (기본 false)
  });
};

export const useFetchLoginICafeInfo = (isLogin: boolean, iCafeId: string, refetchOnWindowFocus?: boolean) => {
  return useQuery({
    ...loginQueryKeys.loginICafeInfo.info(iCafeId),
    meta: { requiresAuth: true }, // 로그인했을 때만 호출해야 하는 api임을 나타내는 메타정보 정의함
    enabled: isLogin && !!iCafeId,
    refetchOnWindowFocus: refetchOnWindowFocus ?? false, // 메인 - 개인화영역 갱신 위해 메인만 포커스 시 리패치 트리거 추가
  });
};
